import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import { FEATURES } from '@Constants/rx-ui/constants';
import Auth from '@Services/Auth';

import { commaFormatNumber } from '@Utils/String';
import { mapContextToTitle } from '@Containers/ResultToolbar/ResultToolbarConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import Footer from '@Components/Footer';
import Page from '@Containers/Page';
import ResultsListComponent from '@Components/Results/results-list.component';
import BreadcrumbsContainer from '@Containers/Breadcrumbs';
import ResultsFilters from '@Containers/ResultsFilters';
import ResultToolbarContainer from '@Containers/ResultToolbar';
import ResultsPaginationContainer from '@Containers/ResultsPagination';
import IntegratedFeedbackContainer from '@Components/IntegratedFeedback/IntegratedFeedbackContainer';
import DatabaseSwitcherTabs from '@Containers/ResultToolbar/DatabaseSwitcher';

import './Results.scss';

export default function Results({
  stateParamsReady,
  isGridView,
  toggleGridView,
  availableDatabases,
  currentDatabase,
  isDbSwitcherVisible,
  selectDatabase,
  goToResultPage,
  supportsReactions,
  showAllPreparations,
  metadata,
  context,
  results,
  searchType,
  originalSearchType,
  currentPageSize,
  resultsLoading,
  paginationVisible,
  showConditions,
  toggleConditions,
  recordCountOrTotalResult,
  isLimitDisabled,
  limitTo,
  isExportEnabled,
  openExportDialog,

  // Selection
  isAllResultsChecked,
  isAllResultsCheckedPartial,
  setAllResultsChecked,
  setAllResultsUnchecked,
  checkedItemsNumber,
}) {
  const [hideIntegratedFeedback, setHideIntegratedFeedback] = useState(false);
  const currentUser = (Auth && Auth.getUser()) || {};
  const disabledTracking = get(currentUser, 'details.disableTracking', {});
  const integratedFeedbackEnabled = Auth.isFeatureEnabled(FEATURES.enableIntegratedFeedback) && !disabledTracking.INTEGRATED_FEEDBACK;
  const intFeedbackBottom = Auth.isFeatureEnabled(FEATURES.bottomIntegratedFeedback);
  const intFeedbackBoth = Auth.isFeatureEnabled(FEATURES.topAndBottomIntegratedFeedback);
  const resultsWrapperAttrs = {
    id: `results-tabpanel-${context}-${metadata.databaseId}`,
    role: 'tabpanel',
    'aria-labeledby': `databases-tab-${context}-${metadata.databaseId}`,
  };

  function getIntegratedFeedbackComponent() {
    return <IntegratedFeedbackContainer searchType={searchType} onHide={setHideIntegratedFeedback} />;
  }

  return (
    <Page id="results-page" className={cn('results-page-container', { 'grid-tiles-view': isGridView })} withFooter={false}>
      <BreadcrumbsContainer />
      <ResultsFilters />
      <section
        id="results-container"
        className={cn('results-container', 'results-container--substance-result-optimization')}
        aria-label={`Results: ${commaFormatNumber(recordCountOrTotalResult || '')} ${mapContextToTitle[context]}`}>
        <div className="results-wrapper" {...resultsWrapperAttrs}>
          <div className="results-list-wrapper-inner">
            {stateParamsReady ? (
              <ResultToolbarContainer
                isListView={!isGridView}
                togglerResultView={toggleGridView}
                availableDatabases={availableDatabases}
                isDbSwitcherVisible={isDbSwitcherVisible}
                currentDatabase={currentDatabase}
                selectDatabase={selectDatabase}
                goToResultPage={goToResultPage}
                supportsReactions={supportsReactions}
                showAllPreparations={showAllPreparations}
                showConditions={showConditions}
                toggleConditions={toggleConditions}
                checkAllItems={setAllResultsChecked}
                uncheckAllItems={setAllResultsUnchecked}
                allItemsSelected={isAllResultsChecked}
                someItemsSelected={isAllResultsCheckedPartial}
                currentSelectedItems={checkedItemsNumber}
                disableLimit={isLimitDisabled}
                limitTo={limitTo}
                isExportEnabled={isExportEnabled}
                openExportDialog={openExportDialog}>
                <DatabaseSwitcherTabs
                  availableDatabases={availableDatabases}
                  isDbSwitcherVisible={isDbSwitcherVisible}
                  currentDatabase={currentDatabase}
                  selectDatabase={selectDatabase}
                />
              </ResultToolbarContainer>
            ) : null}

            <div className="results-content">
              {!hideIntegratedFeedback &&
                integratedFeedbackEnabled &&
                (intFeedbackBoth || !intFeedbackBottom) &&
                getIntegratedFeedbackComponent()}

              <div className="rx-list-wrapper u-els-margin-top-1o2">
                <ResultsListComponent
                  metadata={metadata}
                  context={context}
                  results={results}
                  gridView={isGridView}
                  searchType={searchType}
                  originalSearchType={originalSearchType}
                  size={currentPageSize}
                />
              </div>

              {resultsLoading && (
                <div className="item-placeholder z-index-100">
                  <SimpleSpinner className="e2e-results-spinner rx-loader--container__relative" />
                </div>
              )}

              {!hideIntegratedFeedback &&
                integratedFeedbackEnabled &&
                (intFeedbackBoth || intFeedbackBottom) &&
                getIntegratedFeedbackComponent()}

              {paginationVisible && <ResultsPaginationContainer />}
              <Footer />
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
}

Results.propTypes = {
  stateParamsReady: PropTypes.bool,
  isGridView: PropTypes.bool,
  toggleGridView: PropTypes.func.isRequired,
  availableDatabases: PropTypes.array,
  currentDatabase: PropTypes.object,
  isDbSwitcherVisible: PropTypes.bool,
  selectDatabase: PropTypes.func.isRequired,
  goToResultPage: PropTypes.func.isRequired,
  supportsReactions: PropTypes.bool,
  showAllPreparations: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
  context: PropTypes.string,
  results: PropTypes.array.isRequired,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  currentPageSize: PropTypes.number,
  resultsLoading: PropTypes.bool,
  paginationVisible: PropTypes.bool,
  showConditions: PropTypes.bool,
  toggleConditions: PropTypes.func.isRequired,
  recordCountOrTotalResult: PropTypes.number,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func.isRequired,
  isExportEnabled: PropTypes.bool,
  openExportDialog: PropTypes.func.isRequired,

  // Selection
  isAllResultsChecked: PropTypes.bool,
  isAllResultsCheckedPartial: PropTypes.bool,
  setAllResultsChecked: PropTypes.func.isRequired,
  setAllResultsUnchecked: PropTypes.func.isRequired,
  checkedItemsNumber: PropTypes.number,
};
